import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hide-on-scroll-down nav visibility hook
 *
 * @returns { isHidden: boolean, navRef: (element: HTMLElement | null) => void }:
 *
 * @note
 * - `isHidden` is `true` only when **all four** gates align:
 *     1. `enabled` (the consumer opted in — e.g. routeNavigation's
 *        `visibilityControlledByScroll`),
 *     2. `hasCrossedViewport` (the passed-in flag indicates the user
 *        has scrolled past the viewport threshold),
 *     3. The last scroll movement was downward by more than the delta
 *        threshold and the page is tall enough to scroll past one full
 *        viewport, AND
 *     4. Focus is **not** currently inside the nav.
 *  - `navRef` is a callback ref the consumer attaches to its `<nav>` element
 *     so the focus listener can scope its containment check correctly without
 *     re-attaching on every render.
 */
const SCROLL_DELTA_THRESHOLD_PX = 10;
// A page must offer at least one half of a viewport of scrollable distance
// beyond the initial viewport for the hide to engage.
const MIN_SCROLLABLE_RATIO_FOR_HIDE = 1.5;

function isPageTallEnoughToHide(): boolean {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return false;
  }

  const documentHeight = document.documentElement.scrollHeight;
  const viewportHeight = window.innerHeight;

  return documentHeight > viewportHeight * MIN_SCROLLABLE_RATIO_FOR_HIDE;
}

type UseScrollControlledVisibilityOptions = {
  enabled: boolean;
  hasCrossedViewport: boolean;
};

type UseScrollControlledVisibilityResult = {
  isHidden: boolean;
  navRef: (element: HTMLElement | null) => void;
};

function useScrollControlledVisibility({
  enabled,
  hasCrossedViewport,
}: UseScrollControlledVisibilityOptions): UseScrollControlledVisibilityResult {
  const [internalHidden, setInternalHidden] = useState(false);
  const [focusInsideNav, setFocusInsideNav] = useState(false);
  const navElementRef = useRef<HTMLElement | null>(null);
  const lastScrollYRef = useRef(0);

  const navRef = useCallback((element: HTMLElement | null) => {
    navElementRef.current = element;
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    lastScrollYRef.current = window.scrollY;

    function handleScroll() {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollYRef.current;
      if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD_PX) {
        return;
      }
      lastScrollYRef.current = currentY;

      setInternalHidden(isPageTallEnoughToHide() ? delta > 0 : false);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof document === "undefined") {
      return;
    }

    function handleFocusIn(event: FocusEvent) {
      const target = event.target as Node | null;
      const navElement = navElementRef.current;
      if (navElement && target && navElement.contains(target)) {
        setFocusInsideNav(true);
      }
    }

    function handleFocusOut(event: FocusEvent) {
      const relatedTarget = event.relatedTarget as Node | null;
      const navElement = navElementRef.current;
      if (
        !relatedTarget ||
        (navElement && !navElement.contains(relatedTarget))
      ) {
        setFocusInsideNav(false);
      }
    }

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, [enabled]);

  const isHidden =
    enabled && !focusInsideNav && hasCrossedViewport && internalHidden;

  return { isHidden, navRef };
}

export { useScrollControlledVisibility };
